import { archiveColumns, type ArchiveRow } from '@Constants/TableColumns';
import { emptyPage } from '@Hooks/useListApi';
import { useOpenFromQuery } from '@Hooks/useOpenFromQuery';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { useDocumentsRepository } from '@Hooks/useRepository';
import { type ApiArchive } from '@Model';
import { ArchivesRepository } from '@Repositories/Documents';
import {
    type ListRequest,
    Table,
    type ToolbarElements,
    unwrapOr,
    useOnTheFlyModal,
    useTableRefetch,
} from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { ArchiveDetail } from './ArchiveDetail';

const ARCHIVES = 'archives';

interface ArchiveListProps {
    toolbarElements?: ToolbarElements;
}

/**
 * The caller's ZIP downloads, newest first; a row, or the archive named in the link, opens in a panel over
 * the list.
 */
// @ai-generated 2026-10-01T05:46:19+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function ArchiveList({ toolbarElements }: ArchiveListProps) {
    const archives = useDocumentsRepository(ArchivesRepository);
    const { openOnTheFlyModal } = useOnTheFlyModal();
    const { refetchRef, refetchTable } = useTableRefetch();
    useRemoteCacheInvalidation(ARCHIVES, refetchTable);

    const loader = useCallback(
        async (listRequest: ListRequest) => {
            const page = unwrapOr(await archives.getList(listRequest), emptyPage<ApiArchive>());
            return {
                ...page,
                items: page.items.map((archive): ArchiveRow => ({ ...archive, id: archive.archive_id })),
            };
        },
        [archives],
    );

    const openArchive = useCallback(
        (archive: ApiArchive) => {
            openOnTheFlyModal({
                label: archive.file_name,
                header: { title: archive.file_name },
                child: <ArchiveDetail archiveId={archive.archive_id} />,
            });
            return true;
        },
        [openOnTheFlyModal],
    );
    const describeArchive = useCallback((id: string) => archives.describe(id), [archives]);
    useOpenFromQuery(describeArchive, openArchive);

    return (
        <Table<ArchiveRow>
            name="ArchiveList"
            columns={archiveColumns}
            loader={loader}
            refetchRef={refetchRef}
            onSelect={openArchive}
            initialSorting={{ columnName: 'created_at', direction: 'desc' }}
            toolbarElements={toolbarElements}
            canCreate={false}
            fullSize
        />
    );
}
